import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Typography, ConfigProvider, theme, message, Tabs, Spin, Switch, Descriptions, Card, Tag, Tooltip, Divider, Row, Col } from 'antd';
import {
  LeftOutlined, DesktopOutlined, MobileOutlined, CheckCircleFilled, SyncOutlined, 
  ExperimentOutlined, CodeOutlined, SettingOutlined, RocketFilled, BugFilled,
  Html5Outlined, FileTextOutlined, SendOutlined, DownloadOutlined, CodeFilled, SafetyCertificateFilled,
  EditOutlined, CloseOutlined, PlayCircleOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import CodeEditor from 'components/shared/CodeEditor';
import templateService from 'services/templateService';

const { Text, Title } = Typography;

const TemplatePreviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [viewMode, setViewMode] = useState('desktop');
  const [activeTab, setActiveTab] = useState('html');

  const [sampleData, setSampleData] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [aiMetadata, setAiMetadata] = useState(null);

  useEffect(() => {
    if (id) loadTemplate(id);
  }, [id]);

  const loadTemplate = async (templateId) => {
    try {
      setLoading(true);
      const response = await templateService.getTemplateById(templateId);
      if (response.success) {
        const tmpl = response.data;
        setTemplate(tmpl);
        
        const html = tmpl.htmlTemplate || tmpl.html_template || '';
        const placeholders = (html.match(/\{\{([^}]+)\}\}/g) || []).map(p => p.replace(/\{\{|\}\}/g, '').trim());
        const sample = {};
        placeholders.forEach(p => { sample[p] = `SAMPLE_${p.toUpperCase()}`; });
        if (Object.keys(sample).length === 0) {
           sample.reportTitle = tmpl.templateName;
           sample.executiveSummary = "AI synthesis demonstration.";
        }
        setSampleData(JSON.stringify(sample, null, 2));
        setPreviewHtml(html);
      }
    } catch (error) {
      message.error(error.message);
      navigate('/template-management');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePreview = async () => {
    try {
      let parsedData;
      try { parsedData = JSON.parse(sampleData); } catch {
        message.error('Invalid JSON structure'); return;
      }
      setGenerating(true);
      setGenerationStatus('SYNTHESIZING...');
      
      const response = await templateService.render({
        templateId: id,
        data: parsedData
      });

      if (response.success) {
        setPreviewHtml(response.data.content);
        setAiMetadata(response.data.metadata);
        message.success('INTELLIGENCE SYNTHESIS COMPLETE');
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = () => {
    const blob = new Blob([previewHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `aura_preview_${id}.html`;
    link.click();
    message.success('EXPORT COMPLETE');
  };

  if (loading) return (
    <LoadingOverlay>
      <Spin size="large" />
      <Text style={{ marginTop: 24, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>Loading Preview...</Text>
    </LoadingOverlay>
  );

  return (
    <ConfigProvider 
      theme={{ 
        algorithm: theme.defaultAlgorithm, 
        token: { 
          colorPrimary: '#0a192f', 
          borderRadius: 8,
          fontFamily: 'Inter, system-ui, sans-serif'
        } 
      }}
    >
      <PageWrapper>
        <AuraHeader>
          <HeaderContent>
            <HeaderLeft>
              <ActionIcon onClick={() => navigate('/template-management')}><LeftOutlined /></ActionIcon>
              <Divider type="vertical" style={{ height: 20, margin: '0 20px', borderColor: '#f1f5f9' }} />
              <TitleGroup>
                <Title level={5} style={{ margin: 0, color: 'var(--aura-navy)', fontWeight: 750, fontSize: 14 }}>
                  {template?.templateName || 'Template Preview'}
                </Title>
              </TitleGroup>
            </HeaderLeft>

            <HeaderRight>
              <Space size="middle">
                <MinimalBtn icon={<EditOutlined />} onClick={() => navigate(`/template-management/edit/${id}`)}>Edit Template</MinimalBtn>
                <MinimalBtn icon={<DownloadOutlined />} onClick={handleExport}>Export HTML</MinimalBtn>
                <CloseBtn icon={<CloseOutlined />} onClick={() => navigate('/template-management')} />
              </Space>
            </HeaderRight>
          </HeaderContent>
        </AuraHeader>

        <ContentGrid>
          <Row gutter={20}>
            <Col span={8}>
              <AuraCard title="Input Context" extra={
                 <DeployBtn type="primary" icon={<PlayCircleOutlined />} onClick={handleGeneratePreview} loading={generating}>
                    Generate Preview
                 </DeployBtn>
              }>
                <CodeEditor
                  language="json"
                  value={sampleData}
                  onChange={setSampleData}
                  height="calc(100vh - 420px)"
                />
              </AuraCard>
              
              <AuraCard style={{ marginTop: 20 }} title="AI Telemetry">
                 {aiMetadata ? (
                   <MetricGrid>
                      <MetricItem>
                        <Text type="secondary">Model</Text>
                        <Text strong style={{ fontSize: 13 }}>{aiMetadata.model}</Text>
                      </MetricItem>
                      <MetricItem>
                        <Text type="secondary">Tokens</Text>
                        <Text strong style={{ fontSize: 13 }}>{aiMetadata.tokensUsed?.toLocaleString()} TK</Text>
                      </MetricItem>
                      <MetricItem>
                        <Text type="secondary">Latency</Text>
                        <Text strong style={{ fontSize: 13 }}>{(aiMetadata.duration / 1000).toFixed(2)}s</Text>
                      </MetricItem>
                   </MetricGrid>
                 ) : (
                   <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>Pending generation...</Text>
                 )}
              </AuraCard>
            </Col>

            <Col span={16}>
              <PreviewWrapper>
                <StyledTabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  items={[
                    {
                      key: 'html',
                      label: <><DesktopOutlined /> Preview</>,
                      children: (
                        <TabPane>
                          <ToolBar>
                            <Space>
                              <ViewportLabels>Viewport</ViewportLabels>
                              <ViewportSwitcher>
                                <ViewBtn $active={viewMode === 'desktop'} onClick={() => setViewMode('desktop')}><DesktopOutlined /></ViewBtn>
                                <ViewBtn $active={viewMode === 'mobile'} onClick={() => setViewMode('mobile')}><MobileOutlined /></ViewBtn>
                              </ViewportSwitcher>
                            </Space>
                            {generating && <StatusIndicator><Spin size="small" /> Generating...</StatusIndicator>}
                          </ToolBar>

                          <IframeWrapper $view={viewMode}>
                            <iframe srcDoc={previewHtml} title="Aura Screen" />
                          </IframeWrapper>
                        </TabPane>
                      )
                    },
                    {
                      key: 'logic',
                      label: <><RocketFilled /> Prompt Logic</>,
                      children: (
                        <TabPane>
                          <SourceBox>
                            <pre>{JSON.stringify(template?.promptDefinition || {}, null, 2)}</pre>
                          </SourceBox>
                        </TabPane>
                      )
                    },
                    {
                       key: 'debug',
                       label: <><BugFilled /> Metadata</>,
                       children: (
                         <TabPane>
                            <MinimalDescriptions bordered column={1} size="small">
                               <Descriptions.Item label="Template ID">{id}</Descriptions.Item>
                               <Descriptions.Item label="Analysis Level">{template?.analysisLevel}</Descriptions.Item>
                               <Descriptions.Item label="Version">V{template?.version || 1}</Descriptions.Item>
                               <Descriptions.Item label="Created By">{template?.createdBy || 'System'}</Descriptions.Item>
                            </MinimalDescriptions>
                         </TabPane>
                       )
                    }
                  ]}
                />
              </PreviewWrapper>
            </Col>
          </Row>
        </ContentGrid>
      </PageWrapper>
    </ConfigProvider>
  );
};

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'Inter', sans-serif;
`;

const LoadingOverlay = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: white;
`;

const AuraHeader = styled.div`
  background: white;
  height: 64px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: 1512px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ActionIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--aura-slate);
  &:hover { background: #f1f5f9; color: var(--aura-navy); }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProBadge = styled.span`
  font-size: 8px;
  font-weight: 950;
  color: #64ffda;
  background: var(--aura-navy);
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.8px;
  margin-bottom: 2px;
  display: inline-block;
  width: fit-content;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const MinimalBtn = styled(Button)`
  font-weight: 700 !important;
  font-size: 11px !important;
  letter-spacing: 0.3px !important;
  height: 36px !important;
  padding: 0 16px !important;
  border-radius: 6px !important;
  border-color: #f1f5f9 !important;
  color: var(--aura-slate) !important;
  margin-right: 12px;
`;

const CloseBtn = styled(Button)`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #f1f5f9;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--aura-slate);
  &:hover { background: #e2e8f0; color: var(--aura-navy); }
`;

const ContentGrid = styled.div`
  max-width: 1512px;
  margin: 0 auto;
  padding: 24px;
`;

const AuraCard = styled(Card)`
  border-radius: 8px !important;
  border: 1px solid #f1f5f9 !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.01) !important;
  
  .ant-card-head {
    min-height: 48px !important;
    border-bottom: 1px solid #f1f5f9 !important;
    padding: 0 16px !important;
  }
  .ant-card-head-title {
    font-size: 14px !important;
    font-weight: 600 !important;
    color: var(--aura-navy) !important;
  }
  .ant-card-body { padding: 16px !important; }
`;

const DeployBtn = styled(Button)`
  height: 28px !important;
  padding: 0 12px !important;
  border-radius: 6px !important;
  font-weight: 800 !important;
  font-size: 10px !important;
  letter-spacing: 0.5px !important;
  background: var(--aura-blue) !important;
  border: none !important;
  &:hover { background: #0284c7 !important; }
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  span:first-child { font-size: 9px; font-weight: 850; color: var(--aura-slate); margin-bottom: 2px; }
`;

const PreviewWrapper = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #f1f5f9;
  box-shadow: 0 1px 4px rgba(0,0,0,0.02);
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    padding: 0 20px;
    margin-bottom: 0;
    &::before { border-bottom: 1px solid #f1f5f9; }
  }
  .ant-tabs-tab {
    padding: 16px 0 !important;
    margin-right: 32px !important;
    &-btn {
      font-weight: 800 !important;
      font-size: 11px !important;
      letter-spacing: 0.5px !important;
      color: var(--aura-slate) !important;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn { color: var(--aura-navy) !important; }
  .ant-tabs-ink-bar { background: var(--aura-navy) !important; height: 2px !important; }
`;

const TabPane = styled.div`
  padding: 20px;
  animation: fadeIn 0.3s ease-out;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const ToolBar = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ViewportLabels = styled.span`
  font-size: 10px;
  font-weight: 850;
  color: var(--aura-slate);
  letter-spacing: 0.5px;
`;

const ViewportSwitcher = styled.div`
  display: flex;
  background: #f1f5f9;
  padding: 2px;
  border-radius: 6px;
`;

const ViewBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? 'var(--aura-navy)' : 'var(--aura-slate)'};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0,0,0,0.05)' : 'none'};
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 10px;
  font-weight: 800;
  color: var(--aura-blue);
  background: #f0f9ff;
  padding: 4px 12px;
  border-radius: 6px;
`;

const IframeWrapper = styled.div`
  background: #f1f5f9;
  padding: 24px;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  iframe {
    width: ${props => props.$view === 'mobile' ? '375px' : '100%'};
    height: calc(100vh - 420px);
    border: none;
    border-radius: 6px;
    background: white;
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const SourceBox = styled.div`
  background: #0d1117;
  color: #c9d1d9;
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  height: calc(100vh - 380px);
  pre { margin: 0; font-family: 'Fira Code', monospace; font-size: 11px; line-height: 1.6; }
`;

const MinimalDescriptions = styled(Descriptions)`
  .ant-descriptions-item-label { font-size: 10px; font-weight: 850; color: var(--aura-slate); width: 140px; background: #fafafa !important; }
  .ant-descriptions-item-content { font-size: 12px; color: var(--aura-navy); font-weight: 500; }
`;

export default TemplatePreviewPage;


